import { useState, useEffect } from 'react';
import { Search, Filter, MessageSquare, Calendar, User, MoreVertical, Trash2, Edit2, ChevronLeft, Eye, Folder, FolderPlus, FolderInput } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { sessionApi, Session } from '@/services/api';
import { useSessionStore } from '@/stores';
import { SessionDetailsModal } from './SessionDetailsModal';
import { ConfirmDeleteModal } from './ConfirmDeleteModal';
import { RenameSessionModal } from './RenameSessionModal';
import { RenameFolderModal } from './RenameFolderModal';
import { MoveSessionModal } from './MoveSessionModal';
import { CreateFolderModal } from './CreateFolderModal';

interface SessionsPageProps {
  onBack: () => void;
}

export function SessionsPage({ onBack }: SessionsPageProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sessions, setSessions] = useState<Session[]>([]);
  const [folders, setFolders] = useState<Array<{ folder_id: string; folder_name: string }>>([]);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingSession, setLoadingSession] = useState(false);
  const [detailsSession, setDetailsSession] = useState<Session | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'session' | 'folder'; item: any } | null>(null);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameSession, setRenameSession] = useState<Session | null>(null);
  const [showRenameFolderModal, setShowRenameFolderModal] = useState(false);
  const [renameFolder, setRenameFolder] = useState<{ folder_id: string; folder_name: string } | null>(null);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveSession, setMoveSession] = useState<Session | null>(null);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);

  const { setSessionId, setSessionName, setChatMessages, setSessionHasBeenSaved, setSavedFolderForSession } = useSessionStore();

  useEffect(() => {
    loadSessions();

    // Check if there's a folder to open from localStorage
    const folderToOpen = localStorage.getItem('open_folder_id');
    if (folderToOpen) {
      setSelectedFolder(folderToOpen);
      localStorage.removeItem('open_folder_id'); // Clean up after use
    }
  }, []);

  const loadSessions = async () => {
    setLoading(true);
    try {
      // Fetch both sessions and folders
      const [fetchedSessions, fetchedFolders] = await Promise.all([
        sessionApi.fetchSessions(),
        sessionApi.fetchFolders()
      ]);

      setFolders(fetchedFolders);

      // Map folder_name to sessions
      const sessionsWithFolderNames = fetchedSessions.map(session => {
        if (session.folder_id) {
          const folder = fetchedFolders.find(f => f.folder_id === session.folder_id);
          return {
            ...session,
            folder_name: folder?.folder_name
          };
        }
        return session;
      });

      // Filter out background sessions
      const filteredSessions = sessionsWithFolderNames.filter(
        s => !s.session_id?.startsWith('background_') && s.session_name !== 'where you left off'
      );
      setSessions(filteredSessions);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';

    // DDB timestamps are in UTC format "2025-10-29 14:29:35.889"
    // Convert to ISO format by replacing space with 'T' and adding 'Z' for UTC
    const isoString = dateString.replace(' ', 'T') + 'Z';
    const date = new Date(isoString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return `Today, ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  const getRelativeTime = (dateString: string) => {
    if (!dateString) return 'N/A';

    // DDB timestamps are in UTC format "2025-10-29 14:29:35.889"
    // Convert to ISO format by replacing space with 'T' and adding 'Z' for UTC
    const isoString = dateString.replace(' ', 'T') + 'Z';
    const date = new Date(isoString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffTime / (1000 * 60));

    if (diffMinutes < 60) {
      return `${diffMinutes}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  // Get selected folder object
  const selectedFolderObj = selectedFolder ? folders.find(f => f.folder_id === selectedFolder) : null;

  // Filter sessions based on search and filter criteria
  const filteredSessions = sessions.filter(session => {
    // First apply search and time filters
    const matchesSearch = searchQuery.trim() === '' ||
                         (session.session_name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
                         (session.session_id?.toLowerCase() || '').includes(searchQuery.toLowerCase());

    let matchesFilter = true;
    if (session.last_modified && filterType !== 'all') {
      if (filterType === 'today') {
        const isoString = session.last_modified.replace(' ', 'T') + 'Z';
        const sessionDate = new Date(isoString);
        const today = new Date();
        matchesFilter = sessionDate.toDateString() === today.toDateString();
      } else if (filterType === 'week') {
        const isoString = session.last_modified.replace(' ', 'T') + 'Z';
        const sessionDate = new Date(isoString);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        matchesFilter = sessionDate >= weekAgo;
      } else if (filterType === 'month') {
        const isoString = session.last_modified.replace(' ', 'T') + 'Z';
        const sessionDate = new Date(isoString);
        const monthAgo = new Date();
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        matchesFilter = sessionDate >= monthAgo;
      }
    }

    // If searching, show all matching sessions regardless of folder
    if (searchQuery.trim() !== '') {
      return matchesSearch && matchesFilter;
    }

    // If not searching and a folder is selected, only show sessions in that folder
    if (selectedFolder) {
      if (session.folder_id !== selectedFolder) return false;
    } else {
      // If not searching and no folder selected, only show sessions NOT in any folder
      if (session.folder_id) return false;
    }

    return matchesSearch && matchesFilter;
  });

  // Filter folders based on search
  // Only show folders when not searching and no folder is selected
  const filteredFolders = !selectedFolder && searchQuery.trim() === '' ? folders.filter(folder => {
    return true; // Show all folders when not searching
  }) : [];

  const handleSessionClick = async (session: Session) => {
    setLoadingSession(true);
    try {
      // Fetch full session with chat history
      const fullSession = await sessionApi.fetchSessionWithHistory(session.session_id);

      if (fullSession) {
        // Update session store
        setSessionId(fullSession.session_id);
        setSessionName(fullSession.session_name);
        setSessionHasBeenSaved(true);

        // Set folder name if session is in a folder
        if (fullSession.folder_id) {
          const folder = folders.find(f => f.folder_id === fullSession.folder_id);
          if (folder) {
            setSavedFolderForSession(folder.folder_name);
            localStorage.setItem('folder_name', folder.folder_name);
          }
        }

        // Update chat messages if available
        if (fullSession.chat_history && Array.isArray(fullSession.chat_history)) {
          setChatMessages(normalizeChatHistory(fullSession.chat_history) as any);
        }

        // Update session context in localStorage if available
        if (fullSession.session_context) {
          localStorage.setItem('session_context', JSON.stringify(fullSession.session_context));

          // Update active drafts from session context
          if (fullSession.session_context.current_drafts) {
            console.log('[SessionsPage] Loading session with drafts:', fullSession.session_context.current_drafts);
            localStorage.setItem('active_drafts', JSON.stringify(fullSession.session_context.current_drafts));
          } else {
            console.log('[SessionsPage] Loading session with no drafts, clearing active_drafts');
            // Clear active drafts if session has none
            localStorage.setItem('active_drafts', JSON.stringify([]));
          }

          window.dispatchEvent(new Event('storage-update'));
        }

        // Navigate back to chat
        onBack();
      }
    } catch (error) {
      console.error('Failed to load session:', error);
      alert('Failed to load session. Please try again.');
    } finally {
      setLoadingSession(false);
    }
  };

  const handleRenameSession = (session: Session, e: React.MouseEvent) => {
    e.stopPropagation();
    setRenameSession(session);
    setShowRenameModal(true);
  };

  const handleRenameConfirm = async (newName: string) => {
    if (!renameSession) return;

    await sessionApi.renameSession(
      renameSession.session_id,
      newName
    );

    await loadSessions();
    window.dispatchEvent(new Event('sessions-updated'));
  };

  const handleMoveSession = (session: Session, e: React.MouseEvent) => {
    e.stopPropagation();
    setMoveSession(session);
    setShowMoveModal(true);
  };

  const handleMoveConfirm = async (newFolderId: string | null) => {
    if (!moveSession) return;

    await sessionApi.moveSessionToFolder(
      moveSession.session_id,
      moveSession.folder_id || null,
      newFolderId
    );

    await loadSessions();
    window.dispatchEvent(new Event('sessions-updated'));
  };

  const handleCreateFolder = async (folderName: string) => {
    await sessionApi.createFolder(folderName);
    await loadSessions();
    window.dispatchEvent(new Event('sessions-updated'));
  };

  const handleRenameFolderClick = (folder: { folder_id: string; folder_name: string }, e: React.MouseEvent) => {
    e.stopPropagation();
    setRenameFolder(folder);
    setShowRenameFolderModal(true);
  };

  const handleRenameFolderConfirm = async (newName: string) => {
    if (!renameFolder) return;

    await sessionApi.renameFolder(
      renameFolder.folder_id,
      newName
    );

    await loadSessions();
    window.dispatchEvent(new Event('sessions-updated'));
  };

  const handleDeleteSession = (session: Session, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteTarget({ type: 'session', item: session });
    setShowDeleteModal(true);
  };

  const handleDeleteFolder = (folder: { folder_id: string; folder_name: string }, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteTarget({ type: 'folder', item: folder });
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      if (deleteTarget.type === 'session') {
        await sessionApi.deleteSession(deleteTarget.item.session_id);
        await loadSessions();
        window.dispatchEvent(new Event('sessions-updated'));
      } else if (deleteTarget.type === 'folder') {
        await sessionApi.deleteFolder(deleteTarget.item.folder_id);

        // If currently viewing this folder, navigate back to all sessions
        if (selectedFolder === deleteTarget.item.folder_id) {
          setSelectedFolder(null);
        }

        await loadSessions();
        window.dispatchEvent(new Event('sessions-updated'));
      }
    } catch (error) {
      console.error(`Failed to delete ${deleteTarget.type}:`, error);
      alert(`Failed to delete ${deleteTarget.type}. Please try again.`);
    } finally {
      setDeleteTarget(null);
    }
  };

  const getDeleteModalContent = () => {
    if (!deleteTarget) return { title: '', description: '' };

    if (deleteTarget.type === 'session') {
      return {
        title: 'Delete Session',
        description: `Are you sure you want to delete session "${deleteTarget.item.session_name}"? This action cannot be undone.`,
      };
    } else {
      const sessionsInFolder = sessions.filter(s => s.folder_id === deleteTarget.item.folder_id).length;
      return {
        title: 'Delete Folder',
        description: sessionsInFolder > 0
          ? `Are you sure you want to delete folder "${deleteTarget.item.folder_name}"? The ${sessionsInFolder} session(s) inside will not be deleted and will be moved to "No folder". This action cannot be undone.`
          : `Are you sure you want to delete folder "${deleteTarget.item.folder_name}"? This action cannot be undone.`,
      };
    }
  };

  return (
    <div className="flex flex-col h-full bg-white overflow-hidden">
      {/* Toolbar */}
      <div className="flex-shrink-0 border-b border-gray-200 px-6 py-4 bg-white">
        <div className="flex items-center gap-4 mb-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={selectedFolder ? () => setSelectedFolder(null) : onBack}
            className="h-8 w-8"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>

          {selectedFolder ? (
            <div className="flex items-center gap-2 flex-1">
              <Folder className="w-6 h-6 text-yellow-500" />
              <h1 className="text-2xl font-semibold text-gray-900">{selectedFolderObj?.folder_name || 'Folder'}</h1>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 ml-2">
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white border border-gray-200 shadow-lg">
                  <DropdownMenuItem
                    onClick={(e) => selectedFolderObj && handleRenameFolderClick(selectedFolderObj, e)}
                    className="text-gray-700 hover:bg-gray-100 cursor-pointer"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Rename
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => selectedFolderObj && handleDeleteFolder(selectedFolderObj, e)}
                    className="text-red-600 hover:bg-gray-100 cursor-pointer"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Folder
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <h1 className="text-2xl font-semibold text-gray-900">All Sessions</h1>
          )}
        </div>

        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search sessions by name or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            {!selectedFolder && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCreateFolderModal(true)}
                className="gap-1.5"
              >
                <FolderPlus className="w-4 h-4" />
                New Folder
              </Button>
            )}
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[180px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by time" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center gap-2 text-sm">
          {selectedFolder ? (
            <span className="text-gray-600">{filteredSessions.length} sessions in folder</span>
          ) : (
            <>
              <span className="text-gray-600">{filteredSessions.length} sessions</span>
              <span className="text-gray-300">•</span>
              <span className="text-gray-600">{filteredFolders.length} folders</span>
            </>
          )}
          <span className="text-gray-300">•</span>
          <span className="text-gray-600">Last updated: {new Date().toLocaleDateString()}</span>
        </div>
      </div>

      {/* Sessions List */}
      <div className="flex-1 overflow-hidden max-w-[calc(100vw-20px)]">
        <ScrollArea className="h-full">
          <div className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Loading sessions...</p>
              </div>
            ) : loadingSession ? (
              <div className="text-center py-12">
                <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3 animate-pulse" />
                <p className="text-gray-500">Loading session...</p>
              </div>
            ) : (
              <div className="space-y-2">
                {/* Folders (only shown when no folder is selected) */}
                {!selectedFolder && filteredFolders.map((folder) => (
                  <div
                    key={folder.folder_id}
                    className="max-w-[calc(100vw-60px)] mr-2 border border-gray-200 rounded-lg p-4 hover:border-yellow-400 hover:shadow-sm transition-all cursor-pointer group bg-yellow-50/30"
                    onClick={() => setSelectedFolder(folder.folder_id)}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Folder className="w-6 h-6 text-yellow-600" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-gray-900 line-clamp-1 font-medium">{folder.folder_name}</h3>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-gray-500">
                              <span>{sessions.filter(s => s.folder_id === folder.folder_id).length} sessions</span>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="bg-white border border-gray-200 shadow-lg">
                                <DropdownMenuItem
                                  onClick={(e) => handleRenameFolderClick(folder, e)}
                                  className="text-gray-700 hover:bg-gray-100 cursor-pointer"
                                >
                                  <Edit2 className="w-4 h-4 mr-2" />
                                  Rename
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={(e) => handleDeleteFolder(folder, e)}
                                  className="text-red-600 hover:bg-gray-100 cursor-pointer"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete Folder
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Sessions */}
                {filteredSessions.map((session) => (
                  <div
                    key={session.session_id}
                    className="max-w-[calc(100vw-60px)] mr-2 border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer group"
                    onClick={() => handleSessionClick(session)}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MessageSquare className="w-6 h-6 text-blue-600" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-gray-900 line-clamp-1 font-medium">{session.session_name}</h3>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-gray-500">
                              <span>ID: {session.session_id}</span>
                              <span>•</span>
                              <Badge variant="outline" className="bg-gray-100 text-gray-700 border-gray-200 text-xs">
                                {session.username}
                              </Badge>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDetailsSession(session);
                                setShowDetailsModal(true);
                              }}
                              className="opacity-0 group-hover:opacity-100"
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </Button>

                            <DropdownMenu>
                              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-white border border-gray-200 shadow-lg">
                              <DropdownMenuItem
                                onClick={(e) => handleRenameSession(session, e)}
                                className="text-gray-700 hover:bg-gray-100 cursor-pointer"
                              >
                                <Edit2 className="w-4 h-4 mr-2" />
                                Rename
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => handleMoveSession(session, e)}
                                className="text-gray-700 hover:bg-gray-100 cursor-pointer"
                              >
                                <FolderInput className="w-4 h-4 mr-2" />
                                Move to Folder
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => handleDeleteSession(session, e)}
                                className="text-red-600 hover:bg-gray-100 cursor-pointer"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          </div>
                        </div>

                        <div className="flex items-center gap-6 text-xs text-gray-500">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            Created: {formatDate(session.created)}
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            Modified: {getRelativeTime(session.last_modified)}
                          </div>
                          {session.folder_name && (
                            <div className="flex items-center gap-1 text-blue-600">
                              📁 Folder: {session.folder_name}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!loading && filteredSessions.length === 0 && filteredFolders.length === 0 && (
              <div className="text-center py-12">
                <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">{selectedFolder ? 'No sessions in this folder' : 'No sessions or folders found'}</p>
                <p className="text-sm text-gray-400 mt-1">Try adjusting your search or filters</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Session Details Modal */}
      <SessionDetailsModal
        open={showDetailsModal}
        onOpenChange={setShowDetailsModal}
        session={detailsSession}
      />

      {/* Confirm Delete Modal */}
      <ConfirmDeleteModal
        open={showDeleteModal}
        onOpenChange={setShowDeleteModal}
        onConfirm={handleConfirmDelete}
        title={getDeleteModalContent().title}
        description={getDeleteModalContent().description}
      />

      {/* Rename Session Modal */}
      {renameSession && (
        <RenameSessionModal
          open={showRenameModal}
          onOpenChange={setShowRenameModal}
          currentName={renameSession.session_name}
          onRename={handleRenameConfirm}
        />
      )}

      {/* Rename Folder Modal */}
      {renameFolder && (
        <RenameFolderModal
          open={showRenameFolderModal}
          onOpenChange={setShowRenameFolderModal}
          currentName={renameFolder.folder_name}
          onRename={handleRenameFolderConfirm}
        />
      )}

      {/* Move Session Modal */}
      {moveSession && (
        <MoveSessionModal
          open={showMoveModal}
          onOpenChange={setShowMoveModal}
          sessionName={moveSession.session_name}
          currentFolderId={moveSession.folder_id || null}
          onMove={handleMoveConfirm}
        />
      )}

      {/* Create Folder Modal */}
      <CreateFolderModal
        open={showCreateFolderModal}
        onOpenChange={setShowCreateFolderModal}
        onCreate={handleCreateFolder}
      />
    </div>
  );
}
const normalizeChatHistory = (history: any[] = []) => {
  return history.map((message) => ({
    role: (message.role === 'assistant' || message.role === 'system' ? message.role : 'user') as 'user' | 'assistant' | 'system',
    content: message.content ?? '',
    receiveTime: message.receiveTime,
    sendTime: message.sendTime,
    message_context: message.message_context,
    session_context: message.session_context,
    token_usage: message.token_usage,
  }));
};
